import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Button from 'reactstrap/lib/Button';
import ButtonDropdown from 'reactstrap/lib/ButtonDropdown';
import DropdownItem from 'reactstrap/lib/DropdownItem';
import DropdownMenu from 'reactstrap/lib/DropdownMenu';
import DropdownToggle from 'reactstrap/lib/DropdownToggle';

import FormModal from 'Lib/components/form/FormModal';
import SavingButton from 'Lib/components/form/SavingButton';
import Select from 'Lib/components/form/Select';
import TextField from 'Lib/components/form/TextField';
import withForm from 'Lib/components/hoc/withForm';
import PromptModal from 'Lib/components/modals/PromptModal';
import useApi from 'Lib/hooks/useApi';
import filterParameters from 'Lib/utilities/filterParameters';
import isEmpty from 'Lib/utilities/isEmpty';

const SavedSearches = props => {
    const history = useHistory();

    const [isAdding, setIsAdding] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [dropdownOpen, setDropdown] = useState(false);

    const [savedSearches, setSavedSearches] = useState([]);
    const [selectedSearch, setSelectedSearch] = useState({});
    const [value, setValue] = useState(null);

    const createList = useApi(props.apis.createSavedList);
    const deleteList = useApi(props.apis.deleteSavedList);
    const fetchLists = useApi(props.apis.getSavedLists);
    const updateList = useApi(props.apis.updateSavedList);

    useEffect(() => {
        fetchLists({ listReportTypeID: props.listTypeID, pageSize: 10000 }).then(response =>
            setSavedSearches(response.data?.map(({ savedList }) => ({ label: savedList.name, value: savedList.savedListID, obj: savedList })))
        );
    }, []);

    const handleSave = (values, form) => {
        const { stringified } = filterParameters();
        const data = {
            ...values,
            /*
				We care about storing everything besides page and it's associated number + ampersand &, examples below

				1. ?leaseStatusIDs=2&page=1&pageSize=15 --> removes '&page=1'; has ampersand before and after but only removes the first
				2. ?leaseStatusIDs=2&pageSize=15&page=5 --> removes '&page=5'; has ampersand before and removes it
				3. ?page=5&leaseStatusIDs=2&pageSize=15 --> remove 'page=5&'; has ampersand after and removes it
			*/
            filters: `?${stringified}`.replace(/page=\d+&?|&?page=\d+/, ''),
            listReportTypeID: props.listTypeID
        };

        if (isAdding) {
            props.form.handlePromise(form, [createList(data)]).then(response => {
                const { savedList } = response.data;
                const createdSearch = { label: savedList.name, value: savedList.savedListID, obj: savedList };

                setIsAdding(false);
                setSavedSearches(prevState => [...prevState, createdSearch]);
                setSelectedSearch(createdSearch);
                setValue(savedList.savedListID);
            });
        } else {
            // `value` represents the savedListID of the selected search
            props.form.handlePromise(form, [updateList(value, data)]).then(response => {
                const { savedList } = response.data;
                const createdSearch = { label: savedList.name, value: savedList.savedListID, obj: savedList };

                setIsUpdating(false);
                setSavedSearches(prevState =>
                    prevState.map(search => {
                        if (search.value == savedList.savedListID) {
                            return createdSearch;
                        } else return search;
                    })
                );
                setSelectedSearch(createdSearch);
            });
        }
    };

    const handleDelete = prompt => {
        prompt.setProcessing(true);

        deleteList(value)
            .then(response => {
                prompt.setProcessing(false);

                setIsDeleting(false);
                setValue(null);
                setSelectedSearch({});
                setSavedSearches(prevState => prevState.filter(search => search.value != response.data.savedListID));
            })
            .catch(error => {
                prompt.setProcessing(false);
                props.notify({ error });
            });
    };

    const applyFilter = option => {
        if (option) {
            if (option.obj.filters) history.push(option.obj.filters);
            const { parsed } = filterParameters();
            const params = [parsed, parsed.page, parsed.pageSize];

            setValue(option.value);
            setSelectedSearch(option);

            props.setFilterValues(...params);
            props.search(...params);
            props.setRerenderCount(prevCount => prevCount + 1);
        } else {
            setValue(null);
            setSelectedSearch({});
        }
    };

    const toggleAdd = () => setIsAdding(prevState => !prevState);
    const toggleDelete = () => setIsDeleting(prevState => !prevState);
    const toggleUpdate = () => setIsUpdating(prevState => !prevState);
    const toggleDropdown = () => setDropdown(prevState => !prevState);

    const DeleteModal = () => (
        <PromptModal
            isOpen={isDeleting}
            onClose={toggleDelete}
            header="Delete Saved Search"
            buttons={prompt =>
                prompt.isProcessing ? (
                    <SavingButton color="danger" text="Deleting..." />
                ) : (
                    <>
                        <Button color="danger" onClick={() => handleDelete(prompt)}>
                            Delete
                        </Button>
                        <Button onClick={prompt.close}>Cancel</Button>
                    </>
                )
            }
        >
            Are you sure you want to delete your search, {selectedSearch.label}?
        </PromptModal>
    );

    const AddSearch = () => (
        <FormModal
            enableReinitialize
            title="Add Search"
            isOpen={isAdding}
            toggle={toggleAdd}
            onSubmit={handleSave}
            render={() => <TextField id="name" name="name" label="Name" isRequired />}
        />
    );

    const UpdateSearch = () => (
        <FormModal
            enableReinitialize
            title="Update Search"
            isOpen={isUpdating}
            initialValues={{ name: selectedSearch.label }}
            toggle={toggleUpdate}
            onSubmit={handleSave}
            render={() => <TextField id="name" name="name" label="Name" isRequired />}
        />
    );

    return (
        <div className="searchable-list-saved-search">
            {/* SAVED SEARCHES */}
            <div className="searchable-list-saved-search__select">
                <Select placeholder="Saved searches" options={savedSearches} onChange={applyFilter} value={value} />
            </div>

            {/* OPTIONS */}
            <ButtonDropdown className="searchable-list-saved-search__options" isOpen={dropdownOpen} toggle={toggleDropdown}>
                <DropdownToggle color="success">
                    <i className="fa fa-cog" aria-hidden="true" />
                </DropdownToggle>
                <DropdownMenu>
                    <DropdownItem onClick={toggleAdd}>Add</DropdownItem>
                    {!isEmpty(selectedSearch) && (
                        <>
                            <DropdownItem onClick={toggleUpdate}>Edit</DropdownItem>
                            <DropdownItem onClick={toggleDelete}>Delete</DropdownItem>
                        </>
                    )}
                </DropdownMenu>
            </ButtonDropdown>

            {/* MODALS */}
            <DeleteModal />
            <AddSearch />
            <UpdateSearch />
        </div>
    );
};

export default withForm(SavedSearches);
